/**
 * Navigation utilities for auth redirects.
 *
 * Centralizes the "return to original page after login" logic so the
 * safety check (preventing open redirects) lives in one place.
 */
import { ROUTES } from "../config/constants";

/**
 * Extract a safe return path from a "from" query parameter.
 * Returns the path if it's a same-origin relative URL, or the fallback otherwise.
 *
 * Security: rejects protocol-relative URLs ("//evil.com") and non-path values.
 *
 * @param {string|null} from - Value of the "from" search param
 * @param {string} [fallback] - Default path when from is missing/unsafe.
 *   Defaults to ROUTES.AUTHENTICATED_HOME so the fallback stays in sync with
 *   the rest of the routing config without a second hardcoded "/dashboard".
 * @returns {string} Safe path to navigate to
 */
export function getSafeReturnPath(from, fallback = ROUTES.AUTHENTICATED_HOME) {
  if (from && from.startsWith("/") && !from.startsWith("//")) {
    return from;
  }
  return fallback;
}

/**
 * Build a path to the widget analytics page.
 * Kept here (not in ROUTES) because ROUTES is pure data — all string constants.
 * Dynamic path builders that take parameters live alongside the other navigation
 * utilities in this file.
 *
 * The id is percent-encoded so that non-numeric strings (including path
 * traversal sequences like "../../admin") are safely embedded as a single
 * path segment rather than being interpreted by the router.
 *
 * @param {string|number} id - Widget ID. Must be a non-empty string or number.
 * @returns {string} Path to the widget analytics page
 * @throws {TypeError} if id is null, undefined, or an empty string
 */
export function widgetAnalyticsPath(id) {
  if (id == null || id === "") {
    throw new TypeError(
      `widgetAnalyticsPath: id must be a non-empty string or number, got ${JSON.stringify(id)}`,
    );
  }
  return `/widgets/${encodeURIComponent(id)}/analytics`;
}

/**
 * Build a link to another auth page (login <-> register) preserving the
 * "from" query parameter if present.
 *
 * @param {string} basePath - e.g. "/login" or "/register"
 * @param {string|null} from - Current "from" search param value
 * @returns {string} Path with optional "from" param
 */
export function buildAuthLink(basePath, from) {
  if (from) {
    return `${basePath}?from=${encodeURIComponent(from)}`;
  }
  return basePath;
}
